//@ts-nocheck
"use client"

import { Heading, Text } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Link } from "@chakra-ui/react/link"
import { VStack } from "@chakra-ui/react/stack"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { FC } from "react"

import ArrowLink from "@/components/ArrowLink"
import CategoryBadgeList from "@/components/CategoryBadgeList/CategoryBadgeList"
import Img from "@/components/Img"
import Inner from "@/components/Inner"
import NextLink from "@/components/RouteTransitionLink"
import { PostFixed } from "@/types/database"
import { ArrowUpRightIcon } from "@/components/icons"

export interface Card11Props {
  post: PostFixed
  hiddenAuthor?: boolean
  imageFit?: "contain" | "cover"
  priority?: boolean
}

dayjs.extend(customParseFormat)

function parsePostDate(date?: string | Date | null) {
  if (!date) return null

  if (date instanceof Date) {
    const parsedDate = dayjs(date)
    return parsedDate.isValid() ? parsedDate : null
  }

  const parsedDate = dayjs(
    date,
    [
      dayjs.ISO_8601,
      "D MMM YY",
      "D MMM YYYY",
      "DD MMM YY",
      "DD MMM YYYY",
      "YYYY-MM-DD",
    ],
    true
  )

  if (parsedDate.isValid()) {
    return parsedDate
  }

  const fallbackParsedDate = dayjs(date)
  return fallbackParsedDate.isValid() ? fallbackParsedDate : null
}

function getRelativeDateLabel(date?: string | Date | null) {
  const startDate = parsePostDate(date)
  if (!startDate) return ""

  const todayDate = dayjs()
  const totalDays = todayDate.diff(startDate, "day")
  const totalMonths = todayDate.diff(startDate, "month")
  const durationYears = todayDate.diff(startDate, "year")

  if (totalDays <= 0) {
    return "A few minutes ago"
  }

  if (durationYears >= 1) {
    return `${durationYears}${durationYears > 1 ? " years ago" : " year ago"}`
  }

  if (totalMonths >= 1) {
    return `${totalMonths}${totalMonths > 1 ? " months" : " month"} ago`
  }

  return `${totalDays}${totalDays > 1 ? " days" : " day"} ago`
}

const Card11: FC<Card11Props> = ({
  imageFit = "cover",
  post,
  priority = false,
}) => {
  const { category, date_created, image, slug, thumb, title } = post
  const shouldContainImage = imageFit === "contain"
  const isCaseStudy = category?.some((item) => item?.slug === "case-study")
  const href = post.href ?? `/${isCaseStudy ? "case-study" : "article"}/${slug}`

  const defaultThumbnail = process.env.DIRECTUS_DEFAULT_THUMBNAIL || ""
  const parsedPublishedDate = parsePostDate(date_created)
  const publishedDate = parsedPublishedDate
    ? parsedPublishedDate.format("D MMM YY")
    : ""
  const relativeDateLabel = getRelativeDateLabel(date_created)

  return (
    <VStack
      alignItems="flex-start"
      backgroundPosition="top center"
      bg="bg.paper"
      bgPosition="top center"
      bgRepeat="no-repeat"
      bgSize={"cover"}
      className="card-11"
      display="flex"
      flexDirection="column"
      gap={0}
      minHeight="100%"
      overflow="hidden"
      position="relative"
      rounded="0"
    >
      <Box
        _hover={{
          "& .arrow > .label": {
            transform: "translateX(0)",
          },
          "& .arrow > .label > .text": {
            opacity: 1,
          },
          "& .post-card-image": {
            transform: shouldContainImage ? "none" : "scale(1.02)",
          },
        }}
        aspectRatio={shouldContainImage ? "16 / 9" : `${438 / 360}`}
        bg={shouldContainImage ? "bg.subtle" : "bg.muted"}
        flexShrink={0}
        overflow="hidden"
        position="relative"
        width="full"
      >
        <Inner
          default="none"
          name={`work-article-image-${slug}`}
          share="work-image-morph"
        >
          <Img
            alt={title}
            aria-label={title}
            breakpointWidths={{
              sm: 450,
              md: 725,
              lg: 464,
              xl: 480,
              "2xl": 480,
            }}
            className="post-card-image"
            containerProps={{
              bg: shouldContainImage ? "bg.subtle" : undefined,
              height: "100%",
              overflow: "hidden",
              width: "100%",
              zIndex: 1,
            }}
            // urlWidth={120}
            crop={{ aspect: 438 / 360, enabled: !shouldContainImage }}
            //width={[64, 72, 120]}
            height={"100%"}
            objectFit={imageFit}
            priority={priority}
            rounded={0}
            src={thumb?.id || image?.id || defaultThumbnail}
            transform={{ fit: imageFit, format: "webp", quality: 100 }}
            transition="transform 0.3s ease"
          />
        </Inner>
        <Link
          aria-label={"Link to " + title}
          asChild
          backgroundSize="0 0"
          display="block"
          inset={0}
          position="absolute"
          textDecoration="none"
          zIndex={2}
        >
          <NextLink href={href || "#"} transitionTypes={["nav-forward"]}>
            <Box position="absolute" right="2px" top="2px" zIndex={1}>
              <ArrowLink />
            </Box>
          </NextLink>
        </Link>
      </Box>

      <VStack
        alignItems="flex-start"
        bg="bg.paper"
        flex="1"
        gap={3}
        p={6}
        width="full"
      >
        <CategoryBadgeList categories={category} />

        <Text color="fg.muted" fontSize="xs">
          {publishedDate}
          {relativeDateLabel ? (
            <>
              &nbsp;&#8209;&nbsp;
              <Text as="span">{relativeDateLabel}</Text>
            </>
          ) : null}
        </Text>
        <Heading
          _hover={{
            "& > .arrow": {
              opacity: 1,
              transform: "translateX(0)",
              width: "12px",
            },
          }}
          alignItems={"center"}
          as="h3"
          display="inline-flex"
          gap={2}
          size="md"
        >
          <Text
            _hover={{
              textDecoration: "none",
            }}
            as="span"
            asChild
            color="fg"
            fontWeight="semibold"
            noOfLines={2}
            title={title}
          >
            <Link
              as={NextLink}
              href={href || "#"}
              transitionTypes={["nav-forward"]}
            >
              {title}
            </Link>
          </Text>

          <Box
            as={"span"}
            className="arrow"
            color="fg"
            css={{
              "& svg": {
                height: "12px",
                width: "12px",
              },
            }}
            opacity={0}
            rounded="full"
            transform="translateX(100%)"
            transition="transform 0.16s ease-in, width 0.16s ease-in, opacity 0.16s ease-in"
            width="0px"
            zIndex={12}
          >
            <ArrowUpRightIcon />
          </Box>
        </Heading>
        {/* <Flex alignItems="flex-end" justifyContent="space-between" mt="auto"> */}
        {/* <PostCardSaveAction className="relative" /> */}
        {/* </Flex> */}
      </VStack>
    </VStack>
  )
}

export default Card11
